import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Dropdown, Modal, message } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  MoreOutlined,
  RightOutlined,
  ShareAltOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';

import { useModuleActions } from '../../hooks/useModuleActions';
import { fetchJsonWithAuth } from '../../services/authApi';
import { AUTH_URL } from '../../services/apiConfig';
import { roleAllowsAction, isActionValueAllowed } from '../../utils/roleAllowsAction';
import { isRuleActionBlocked, ruleActionDisabledReason, actionDoneState } from '../../utils/actionRuleEngine';

// Same icon vocabulary as ListView's action column, so an action configured
// once in Admin renders identically on every surface that uses this component.
const ACTION_ICON_MAP = {
  delete: <DeleteOutlined />,
  edit: <EditOutlined />,
  mail: <MailOutlined />,
  shareAlt: <ShareAltOutlined />,
  swap: <RightOutlined />,
  thunderbolt: <ThunderboltOutlined />,
  userAdd: <UserAddOutlined />,
  userSwitch: <UserSwitchOutlined />,
};

function templatePart(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'object') return String(value.id ?? value.value ?? '');
  return String(value);
}

function fillRouteTemplate(template, record) {
  return String(template)
    .replace(/\{([^}]+)\}/g, (_, key) => encodeURIComponent(templatePart(record?.[key])))
    .replace(/:([A-Za-z_][\w]*)/g, (_, key) => encodeURIComponent(
      templatePart(record?.[key] ?? (key === '_id' ? record?.id : key === 'id' ? record?._id : undefined)),
    ));
}

function getNestedValue(record, path) {
  return String(path || '').split('.').reduce((value, key) => value?.[key], record);
}

function toMenuItem(action, record, actionRules, scope) {
  // Config-driven "done" terminus: once doneWhen matches (e.g. the submission's
  // onboardingStatus is set), the action is no longer clickable and shows its
  // doneLabel ("Moved to Onboarded") instead of the normal label.
  const done = actionDoneState(action, record, scope);
  // Rule-disabled (not blocked/hidden — see ruleActionDisabledReason): the
  // item stays in the menu, just greyed out, with the rule's description as
  // the reason shown on hover.
  const disabledReason = ruleActionDisabledReason(action.key, record, actionRules, scope);
  const item = {
    key: action.key,
    label: done ? done.label : (action.label || action.key),
    icon: ACTION_ICON_MAP[action.icon] ?? undefined,
    danger: action.danger || undefined,
    disabled: !!done || !!disabledReason,
    title: done ? done.label : (disabledReason || undefined),
  };
  if (action.children?.length) item.children = action.children.map((child) => toMenuItem(child, record, actionRules, scope));
  return item;
}

function flattenActions(actions) {
  const flat = [];
  for (const action of actions) {
    flat.push(action);
    if (action.children?.length) flat.push(...flattenActions(action.children));
  }
  return flat;
}

/**
 * ModuleRowActions — admin-configured row actions for list pages that don't
 * use the generic ListView (e.g. the Onboarding movements list). Reads the
 * same /module-actions contract (rowActionConfig + module action rules) and
 * dispatches with the same semantics as ListView / DetailHeaderCard:
 * navigate (linkTarget "_blank" honored), modal → /popup/<popupKey> route
 * over the current page, api (danger actions get a confirm), copy.
 *
 * Props:
 *   module   — rowActionConfig module key (e.g. "onboarding")
 *   record   — the row's record, used for template filling + rule evaluation
 *   scope    — optional current tab/scope for allowedTabs + scope rules
 *   onDone   — called after an api action succeeds (e.g. to refresh the list)
 *   fallback — rendered when no configured action is visible for this row
 */
export default function ModuleRowActions({ module, record, scope, onDone, fallback = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { actions, actionRules } = useModuleActions(module);

  const isVisible = (action) => {
    if (action.showInList === false) return false;
    if (!roleAllowsAction(action.permission, module)) return false;
    if (action.permissionKey
      && !isActionValueAllowed(getNestedValue(record, `actionPermission.${action.permissionKey}`))) return false;
    if (isRuleActionBlocked(action.key, record, actionRules, scope)) return false;
    if (action.allowedTabs?.length && scope
      && !action.allowedTabs.some((tab) => String(tab).toLowerCase() === String(scope).toLowerCase())) return false;
    return true;
  };

  const visibleActions = (actions ?? [])
    .filter(isVisible)
    .map((action) => (action.children?.length
      ? { ...action, children: action.children.filter(isVisible) }
      : action));

  if (!visibleActions.length) return fallback;

  const runAction = (action) => {
    if (action.actionType === 'copy') {
      const value = getNestedValue(record, action.copyField || 'id') ?? record?._id;
      navigator.clipboard.writeText(templatePart(value))
        .then(() => message.success('Copied to clipboard'))
        .catch(() => message.error('Could not copy to clipboard'));
      return;
    }

    if (action.actionType === 'api' && action.apiPath) {
      const call = () => {
        const apiPath = fillRouteTemplate(action.apiPath, record);
        const method = String(action.apiMethod || 'POST').toUpperCase();
        fetchJsonWithAuth(AUTH_URL, apiPath.startsWith('/') ? apiPath : `/${apiPath}`, {
          method,
          body: ['GET', 'DELETE'].includes(method)
            ? undefined
            : JSON.stringify({ ids: [record?._id ?? record?.id], records: [record] }),
        })
          .then(() => {
            message.success(`${action.label} completed`);
            onDone?.(action);
          })
          .catch((error) => message.error(error.message || `${action.label} failed`));
      };
      if (action.danger) {
        Modal.confirm({
          title: `${action.label}?`,
          content: 'This cannot be undone.',
          okText: action.label,
          okButtonProps: { danger: true },
          onOk: call,
        });
      } else {
        call();
      }
      return;
    }

    if (action.actionType === 'modal') {
      const popupKey = action.popupKey || action.key;
      navigate(`/popup/${popupKey}`, {
        state: {
          background: location,
          selectedIds: [record?._id ?? record?.id],
          selectedRecords: [record],
          moduleName: module,
          popupContext: action.popupState ?? {},
          popupAction: {
            key: action.key,
            apiMethod: action.apiMethod,
            apiPath: action.apiPath,
            defaultTemplateId: action.defaultTemplateId,
            requireJobId: action.requireJobId,
            popupFieldOverrides: action.popupFieldOverrides,
          },
          ...action.popupState,
        },
      });
      return;
    }

    const route = action.routeTemplate || action.route;
    if (route) {
      const resolved = fillRouteTemplate(route, record);
      const normalized = resolved.startsWith('/') ? resolved : `/${resolved}`;
      if (action.linkTarget === '_blank') {
        window.open(normalized, '_blank', 'noopener');
        return;
      }
      navigate(normalized);
    }
  };

  const byKey = new Map(flattenActions(visibleActions).map((a) => [String(a.key), a]));

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: visibleActions.map((action) => toMenuItem(action, record, actionRules, scope)),
        onClick: ({ key, domEvent }) => {
          domEvent?.stopPropagation?.();
          const action = byKey.get(String(key));
          // A "done" action (doneWhen matched) is a disabled success label, not
          // a trigger — never dispatch it even if a click slips through.
          if (action && action.actionType !== 'group' && !actionDoneState(action, record, scope)) runAction(action);
        },
      }}
    >
      <Button
        type="text"
        icon={<MoreOutlined />}
        onClick={(event) => event.stopPropagation()}
        aria-label="Row actions"
      />
    </Dropdown>
  );
}
